import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import * as Icons from 'lucide-react';
import { Lock } from 'lucide-react';
import { habitApiService, CreateHabitRequest, ColorOption } from '../../services/habitApi';
import { aiSuggestionApiService, AiLimitExceededError, HabitSuggestion } from '../../services/aiSuggestionApi';
import { useHabitStore } from '../../store/habitStore';
import { useSubscriptionStore } from '../../store/subscriptionStore';
import { HabitLimitModal } from '../subscription/HabitLimitModal';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { resolveHabitIcon } from '../../utils/iconMapping';
import toast from 'react-hot-toast';

interface CreateHabitFormProps {
  onSuccess?: () => void;
  onUpgrade?: () => void;
  initialSuggestion?: HabitSuggestion;
}

// Popular icons for habits
const POPULAR_ICONS = [
  { name: 'Activity', icon: Icons.Activity },
  { name: 'Book', icon: Icons.Book },
  { name: 'Coffee', icon: Icons.Coffee },
  { name: 'Dumbbell', icon: Icons.Dumbbell },
  { name: 'Heart', icon: Icons.Heart },
  { name: 'Home', icon: Icons.Home },
  { name: 'Moon', icon: Icons.Moon },
  { name: 'Music', icon: Icons.Music },
  { name: 'Run', icon: Icons.Zap },
  { name: 'Smile', icon: Icons.Smile },
  { name: 'Star', icon: Icons.Star },
  { name: 'Sun', icon: Icons.Sun },
  { name: 'Target', icon: Icons.Target },
  { name: 'Tree', icon: Icons.TreePine },
  { name: 'Droplet', icon: Icons.Droplet },
  { name: 'Flame', icon: Icons.Flame },
  { name: 'Apple', icon: Icons.Apple },
  { name: 'Bike', icon: Icons.Bike },
  { name: 'Camera', icon: Icons.Camera },
  { name: 'Clock', icon: Icons.Clock },
  { name: 'Eye', icon: Icons.Eye },
  { name: 'Gift', icon: Icons.Gift },
  { name: 'Headphones', icon: Icons.Headphones },
  { name: 'Leaf', icon: Icons.Leaf },
  { name: 'Lightbulb', icon: Icons.Lightbulb },
  { name: 'Map', icon: Icons.Map },
  { name: 'Palette', icon: Icons.Palette },
  { name: 'Phone', icon: Icons.Phone },
  { name: 'Shield', icon: Icons.Shield },
  { name: 'Umbrella', icon: Icons.Umbrella },
  { name: 'Circle', icon: Icons.Circle },
  { name: 'Square', icon: Icons.Square },
  { name: 'Triangle', icon: Icons.Triangle },
  { name: 'Diamond', icon: Icons.Diamond },
  { name: 'Hexagon', icon: Icons.Hexagon },
  { name: 'Octagon', icon: Icons.Octagon },
];


export default function CreateHabitForm({ onSuccess, onUpgrade, initialSuggestion }: CreateHabitFormProps) {
  const [suggestedColors, setSuggestedColors] = useState<ColorOption[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingSuggestion, setIsGeneratingSuggestion] = useState(false);
  const [showHabitLimitModal, setShowHabitLimitModal] = useState(false);
  const [habitLimitError, setHabitLimitError] = useState<{
    currentCount: number;
    maxAllowed: number;
    subscriptionTier: string;
  } | null>(null);

  const { currentHabitCount, maxHabitsAllowed, aiSuggestionsUsedToday, maxAiSuggestionsPerDay, canUseAiToday } = useFeatureAccess();

  // Get existing habit names for duplicate validation
  const { getActiveHabits } = useHabitStore();
  const existingHabits = getActiveHabits();
  const existingHabitNames = existingHabits.map(habit => habit.name.toLowerCase());

  // Form validation schema with duplicate name validation
  const createHabitSchema = z.object({
    name: z.string()
      .min(1, 'Habit name is required')
      .max(100, 'Name must be 100 characters or less')
      .refine(
        (name) => !existingHabitNames.includes(name.toLowerCase()),
        'A habit with this name already exists'
      ),
    description: z.string().max(500, 'Description must be 500 characters or less').optional(),
    iconName: z.string().min(1, 'Please select an icon'),
    colorCode: z.string().regex(/^#[0-9A-F]{6}$/i, 'Please select a color'),
    frequency: z.string().refine((val) => ['daily', 'weeklySpecificDays', 'weeklyCount'].includes(val), {
      message: 'Please select a frequency'
    }),
    targetStreakLength: z.number().min(1, 'Target streak must be at least 1 day').max(365, 'Target streak must be 365 days or less'),
    specificDays: z.array(z.number()).optional(),
    weeklyCount: z.number().min(1).max(7).optional(),
  });

  type CreateHabitFormData = z.infer<typeof createHabitSchema>;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateHabitFormData>({
    resolver: zodResolver(createHabitSchema),
    defaultValues: {
      name: '',
      description: '',
      iconName: '',
      colorCode: '',
      frequency: 'daily',
      targetStreakLength: 30,
      specificDays: [],
      weeklyCount: 3,
    },
  });

  const watchedFrequency = watch('frequency');
  const watchedColorCode = watch('colorCode');
  const watchedIconName = watch('iconName');
  const watchedSpecificDays = watch('specificDays') || [];
  const watchedName = watch('name');
  const watchedDescription = watch('description');

  // Surface an AI-suggested icon that isn't one of the default tiles, so it shows as selected.
  const selectedIconInGrid = POPULAR_ICONS.some(
    (i) => i.name.toLowerCase() === (watchedIconName || '').toLowerCase()
  );
  const displayedIcons = !selectedIconInGrid && watchedIconName
    ? [{ name: watchedIconName, icon: resolveHabitIcon(watchedIconName) }, ...POPULAR_ICONS]
    : POPULAR_ICONS;

  // Load icons and colors on component mount
  useEffect(() => {
    const loadFormData = async () => {
      try {
        const colors = await habitApiService.getSuggestedColors();
        setSuggestedColors(colors);

        if (initialSuggestion) {
          // Pre-fill all fields from the discovery suggestion
          setValue('name', initialSuggestion.name);
          setValue('description', initialSuggestion.description ?? '');
          setValue('iconName', initialSuggestion.iconName.toLowerCase());
          setValue('colorCode', initialSuggestion.colorCode);
          setValue('targetStreakLength', initialSuggestion.targetStreakLength);

          const frequencyMap: Record<number, 'daily' | 'weeklySpecificDays' | 'weeklyCount'> = {
            0: 'daily',
            1: 'weeklySpecificDays',
            2: 'weeklyCount',
          };
          setValue('frequency', frequencyMap[initialSuggestion.frequency] ?? 'daily');

          if (initialSuggestion.frequency === 1 && initialSuggestion.weeklyDays) {
            setValue('specificDays', initialSuggestion.weeklyDays);
          } else if (initialSuggestion.frequency === 2 && initialSuggestion.weeklyCount) {
            setValue('weeklyCount', initialSuggestion.weeklyCount);
          }
        } else {
          // Set defaults only when no suggestion is provided
          if (colors.length > 0 && !watchedColorCode) {
            setValue('colorCode', colors[0].code);
          }
          if (POPULAR_ICONS.length > 0 && !watchedIconName) {
            setValue('iconName', POPULAR_ICONS[0].name.toLowerCase());
          }
        }
      } catch (error) {
        console.error('Failed to load form data:', error);
        toast.error('Failed to load form options');
      }
    };

    loadFormData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue, initialSuggestion]);

  const handleAiSuggestion = async () => {
    if (!watchedName.trim()) {
      toast.error('Please enter a habit name first');
      return;
    }

    if (!canUseAiToday) {
      toast.error(`Daily AI limit reached (${aiSuggestionsUsedToday}/${maxAiSuggestionsPerDay}). Upgrade for more.`);
      return;
    }

    setIsGeneratingSuggestion(true);

    try {
      const suggestion = await aiSuggestionApiService.getSuggestion({
        title: watchedName,
        context: watchedDescription || undefined,
      });

      // Apply the AI suggestion to the form
      setValue('name', suggestion.name);
      setValue('description', suggestion.description);
      setValue('iconName', suggestion.iconName.toLowerCase());
      setValue('colorCode', suggestion.colorCode);
      setValue('targetStreakLength', suggestion.targetStreakLength);

      // Handle frequency-specific settings
      const frequencyMap: Record<number, 'daily' | 'weeklySpecificDays' | 'weeklyCount'> = {
        0: 'daily',
        1: 'weeklySpecificDays',
        2: 'weeklyCount',
      };

      const mappedFrequency = frequencyMap[suggestion.frequency] || 'daily';
      setValue('frequency', mappedFrequency);

      if (suggestion.frequency === 1 && suggestion.weeklyDays) {
        setValue('specificDays', suggestion.weeklyDays);
      } else if (suggestion.frequency === 2 && suggestion.weeklyCount) {
        setValue('weeklyCount', suggestion.weeklyCount);
      }

      toast.success(`AI suggestion applied! ${suggestion.reasoning}`);

      // Refresh feature access to update AI usage counter
      useSubscriptionStore.getState().loadFeatureAccess();
    } catch (error) {
      console.error('Failed to get AI suggestion:', error);

      if (error instanceof AiLimitExceededError) {
        toast.error(`Daily AI limit reached (${error.usedToday}/${error.dailyLimit}). Upgrade for more.`);
        if (onUpgrade) {
          onUpgrade();
        }
      } else {
        toast.error(error instanceof Error ? error.message : 'Failed to generate AI suggestion');
      }
    } finally {
      setIsGeneratingSuggestion(false);
    }
  };

  const onSubmit = async (data: CreateHabitFormData) => {
    setIsSubmitting(true);
    
    try {
      // Convert string frequency to numeric enum value
      const frequencyMap: Record<string, 0 | 1 | 2 | 3> = {
        'daily': 0,
        'weeklySpecificDays': 1,
        'weeklyCount': 2,
        'intervalBased': 3
      };

      const request: CreateHabitRequest = {
        name: data.name,
        description: data.description || '',
        iconName: data.iconName,
        colorCode: data.colorCode,
        frequency: frequencyMap[data.frequency],
        targetStreakLength: data.targetStreakLength,
      };

      // Add frequency-specific data
      if (data.frequency === 'weeklySpecificDays' && data.specificDays) {
        request.weeklyDays = data.specificDays;
      } else if (data.frequency === 'weeklyCount' && data.weeklyCount) {
        request.weeklyCount = data.weeklyCount;
      }

      await habitApiService.createHabit(request);
      toast.success('Habit created successfully!');
      onSuccess?.();
    } catch (error) {
      console.error('Failed to create habit:', error);
      
      // Handle habit limit exceeded error (402)
      if (error instanceof Error && (error as any).isHabitLimitExceeded) {
        const habitError = error as any;
        setHabitLimitError({
          currentCount: habitError.currentCount || currentHabitCount,
          maxAllowed: habitError.maxAllowed || maxHabitsAllowed,
          subscriptionTier: habitError.subscriptionTier || 'Free'
        });
        setShowHabitLimitModal(true);
        return;
      }
      
      // Handle specific error messages from the backend
      if (error instanceof Error) {
        if (error.message.includes('already exists')) {
          toast.error(error.message);
        } else {
          toast.error('Failed to create habit');
        }
      } else {
        toast.error('Failed to create habit');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDayToggle = (dayIndex: number) => {
    const currentDays = watchedSpecificDays;
    const newDays = currentDays.includes(dayIndex)
      ? currentDays.filter(d => d !== dayIndex)
      : [...currentDays, dayIndex].sort();
    setValue('specificDays', newDays);
  };

  const daysOfWeek = [
    { index: 1, name: 'Monday' },
    { index: 2, name: 'Tuesday' },
    { index: 3, name: 'Wednesday' },
    { index: 4, name: 'Thursday' },
    { index: 5, name: 'Friday' },
    { index: 6, name: 'Saturday' },
    { index: 0, name: 'Sunday' },
  ];

  // Check if user can create more habits
  // -1 means unlimited, so only enforce limit when maxHabitsAllowed > 0
  if (maxHabitsAllowed >= 0 && currentHabitCount >= maxHabitsAllowed) {
    return (
      <div className="p-6">
        <div className={`flex flex-col items-center justify-center py-8 px-6 rounded-lg border-2 border-dashed ${
          currentHabitCount === 0
            ? 'bg-gradient-to-br from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 border-blue-300 dark:border-blue-700'
            : 'bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 border-orange-300 dark:border-orange-700'
        }`}>
          <div className={`flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
            currentHabitCount === 0
              ? 'bg-blue-100 dark:bg-blue-900/30'
              : 'bg-orange-100 dark:bg-orange-900/30'
          }`}>
            <Lock className={`w-8 h-8 ${
              currentHabitCount === 0
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-orange-600 dark:text-orange-400'
            }`} />
          </div>
          
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2 text-center">
            {currentHabitCount === 0 ? 'Create Your First Habit' : 'Habit Limit Reached'}
          </h3>
          
          <p className="text-gray-600 dark:text-gray-400 text-center mb-4 max-w-md">
            {currentHabitCount === 0 
              ? `You can create up to ${maxHabitsAllowed} habits on the free plan. Start building positive habits today!`
              : `You've reached your limit of ${maxHabitsAllowed} habits on the free plan. Upgrade to create unlimited habits and track more of your goals!`
            }
          </p>
          
          <div className="text-center mb-4">
            <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">
              Current Habits: {currentHabitCount}/{maxHabitsAllowed}
            </div>
            <div className="w-32 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all duration-300 ${
                  currentHabitCount === 0 
                    ? 'bg-blue-500' 
                    : currentHabitCount >= maxHabitsAllowed 
                    ? 'bg-red-500' 
                    : 'bg-orange-500'
                }`}
                style={{ width: `${Math.min((currentHabitCount / maxHabitsAllowed) * 100, 100)}%` }}
              />
            </div>
          </div>
          
          {currentHabitCount === 0 ? (
            <button
              type="button"
              onClick={() => {
                // Close the modal and show the form
                window.location.reload(); // Simple refresh for now
              }}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <Icons.Plus className="w-4 h-4 mr-2" />
              Start Creating Habits
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                if (onUpgrade) {
                  onUpgrade();
                } else {
                  // Fallback: show toast and suggest manual navigation
                  toast.success('Please visit the pricing page to upgrade your plan');
                }
              }}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <Icons.Crown className="w-4 h-4 mr-2" />
              Upgrade for Unlimited Habits
            </button>
          )}
          
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-3">
            {currentHabitCount === 0 
              ? 'Free plan includes up to 3 habits'
              : 'Start with a 7-day free trial'
            }
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Habit Name */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Habit Name
            </label>
            <div className="flex items-center gap-2">
              {/* AI Usage Counter */}
              {maxAiSuggestionsPerDay > 0 && (
                <span className={`text-xs ${canUseAiToday ? 'text-gray-500 dark:text-gray-400' : 'text-red-500 dark:text-red-400'}`}>
                  {aiSuggestionsUsedToday}/{maxAiSuggestionsPerDay} remaining
                </span>
              )}
              <button
                type="button"
                onClick={handleAiSuggestion}
                disabled={isGeneratingSuggestion || !watchedName.trim() || !canUseAiToday}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={!canUseAiToday ? 'Daily AI limit reached. Upgrade for more.' : ''}
              >
                {isGeneratingSuggestion ? (
                  <>
                    <Icons.Loader2 size={14} className="animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Icons.Sparkles size={14} />
                    AI Suggest
                  </>
                )}
              </button>
            </div>
          </div>
          <input
            {...register('name')}
            placeholder="Enter habit name"
            className="w-full px-4 py-3 rounded-xl bg-gray-100 dark:bg-gray-700 border-none text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {errors.name && (
            <p className="text-red-500 dark:text-red-400 text-sm mt-1">{errors.name.message}</p>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Description (Optional)
          </label>
          <textarea
            {...register('description')}
            placeholder="Add notes or description"
            rows={3}
            className="w-full px-4 py-3 rounded-xl bg-gray-100 dark:bg-gray-700 border-none text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
          {errors.description && (
            <p className="text-red-500 dark:text-red-400 text-sm mt-1">{errors.description.message}</p>
          )}
        </div>

        {/* Icon Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Icon
          </label>
          <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 gap-3 max-h-40 overflow-y-auto">
            {displayedIcons.map((iconData) => {
              const IconComponent = iconData.icon;
              const isSelected = watchedIconName === iconData.name.toLowerCase();
              
              return (
                <label
                  key={iconData.name}
                  className={`w-10 h-10 rounded-lg border-2 cursor-pointer transition-all flex items-center justify-center ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                  title={iconData.name}
                >
                  <input
                    type="radio"
                    className="sr-only"
                    {...register('iconName')}
                    value={iconData.name.toLowerCase()}
                  />
                  <IconComponent 
                    size={20} 
                    className={isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'} 
                  />
                </label>
              );
            })}
          </div>
          {errors.iconName && (
            <p className="text-red-500 dark:text-red-400 text-sm mt-1">{errors.iconName.message}</p>
          )}
        </div>

        {/* Color Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Color
          </label>
          <div className="flex flex-wrap gap-3">
            {suggestedColors.map((color) => (
              <label
                key={color.code}
                className={`w-10 h-10 rounded-full border cursor-pointer transition-all ${
                  watchedColorCode === color.code
                    ? 'border-[3px] border-white dark:border-gray-800 ring-2 ring-gray-400 dark:ring-gray-500'
                    : 'border-2 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                }`}
                style={{ backgroundColor: color.code }}
                title={color.name}
              >
                <input
                  type="radio"
                  className="sr-only"
                  {...register('colorCode')}
                  value={color.code}
                />
              </label>
            ))}
          </div>
          {errors.colorCode && (
            <p className="text-red-500 dark:text-red-400 text-sm mt-1">{errors.colorCode.message}</p>
          )}
        </div>

        {/* Frequency */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Frequency
          </label>
          <div className="space-y-3">
            <label className={`block p-4 rounded-xl border-2 cursor-pointer transition-all ${
              watchedFrequency === 'daily'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
            }`}>
              <div className="flex items-center">
                <input
                  type="radio"
                  className="sr-only"
                  {...register('frequency')}
                  value="daily"
                />
                <div className={`w-4 h-4 rounded-full border-2 mr-3 ${
                  watchedFrequency === 'daily'
                    ? 'border-blue-500 bg-blue-500'
                    : 'border-gray-300 dark:border-gray-600'
                }`}>
                  {watchedFrequency === 'daily' && (
                    <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5" />
                  )}
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Daily</span>
              </div>
            </label>

            <label className={`block p-4 rounded-xl border-2 cursor-pointer transition-all ${
              watchedFrequency === 'weeklySpecificDays'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
            }`}>
              <div className="flex items-center">
                <input
                  type="radio"
                  className="sr-only"
                  {...register('frequency')}
                  value="weeklySpecificDays"
                />
                <div className={`w-4 h-4 rounded-full border-2 mr-3 ${
                  watchedFrequency === 'weeklySpecificDays'
                    ? 'border-blue-500 bg-blue-500'
                    : 'border-gray-300 dark:border-gray-600'
                }`}>
                  {watchedFrequency === 'weeklySpecificDays' && (
                    <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5" />
                  )}
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Specific days of the week</span>
              </div>
            </label>

            <label className={`block p-4 rounded-xl border-2 cursor-pointer transition-all ${
              watchedFrequency === 'weeklyCount'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
            }`}>
              <div className="flex items-center">
                <input
                  type="radio"
                  className="sr-only"
                  {...register('frequency')}
                  value="weeklyCount"
                />
                <div className={`w-4 h-4 rounded-full border-2 mr-3 ${
                  watchedFrequency === 'weeklyCount'
                    ? 'border-blue-500 bg-blue-500'
                    : 'border-gray-300 dark:border-gray-600'
                }`}>
                  {watchedFrequency === 'weeklyCount' && (
                    <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5" />
                  )}
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">X times per week</span>
              </div>
            </label>
          </div>
          {errors.frequency && (
            <p className="text-red-500 dark:text-red-400 text-sm mt-1">{errors.frequency.message}</p>
          )}
        </div>

        {/* Specific Days Selection */}
        {watchedFrequency === 'weeklySpecificDays' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Select Days
            </label>
            <div className="space-y-2">
              {daysOfWeek.map((day) => (
                <label key={day.index} className="flex items-center p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={watchedSpecificDays.includes(day.index)}
                    onChange={() => handleDayToggle(day.index)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">{day.name}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Weekly Count Input */}
        {watchedFrequency === 'weeklyCount' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Times per week
            </label>
            <input
              {...register('weeklyCount', { valueAsNumber: true })}
              type="number"
              min="1"
              max="7"
              placeholder="e.g., 3"
              className="w-full px-4 py-3 rounded-xl bg-gray-100 dark:bg-gray-700 border-none text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {errors.weeklyCount && (
              <p className="text-red-500 dark:text-red-400 text-sm mt-1">{errors.weeklyCount.message}</p>
            )}
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
              Complete this habit at least {watch('weeklyCount') || 'X'} times each week
            </p>
          </div>
        )}

        {/* Target Streak Length */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Target Streak Length
          </label>
          <input
            {...register('targetStreakLength', { valueAsNumber: true })}
            type="number"
            min="1"
            max="365"
            placeholder="e.g., 30"
            className="w-full px-4 py-3 rounded-xl bg-gray-100 dark:bg-gray-700 border-none text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {errors.targetStreakLength && (
            <p className="text-red-500 dark:text-red-400 text-sm mt-1">{errors.targetStreakLength.message}</p>
          )}
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
            Set your goal for consecutive days (1-365 days)
          </p>
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 dark:disabled:bg-blue-600 text-white font-medium rounded-xl transition-colors disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Creating Habit...' : 'Create Habit'}
          </button>
        </div>
      </form>

      {/* Habit Limit Modal */}
      {showHabitLimitModal && habitLimitError && (
        <HabitLimitModal
          isOpen={showHabitLimitModal}
          onClose={() => setShowHabitLimitModal(false)}
          onUpgrade={() => {
            setShowHabitLimitModal(false);
            if (onUpgrade) {
              onUpgrade();
            } else {
              toast.success('Please visit the pricing page to upgrade your plan');
            }
          }}
          currentCount={habitLimitError.currentCount}
          maxAllowed={habitLimitError.maxAllowed}
          subscriptionTier={habitLimitError.subscriptionTier}
        />
      )}
    </div>
  );
} 